import { useSatuEventTracker } from 'utils/satuEvent'
import AndroidHandler from 'utils/androidHandler'
import { useAuth } from 'utils/auth'
import useSearchQuery from 'app/SearchPage/hooks/useSearchQuery'
import { APP_DEVICE_ID_STORAGE_KEY } from 'configs/android'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import {
  useContentSearchFilter,
  useContentSearchTracker,
} from 'stores/content-search'

import type { TSETrackProfileActivities } from 'types/profile'

type TSETrackEventDefaultAttributes = {
  is_login: [boolean, 'bool']
  is_webview: [boolean, 'bool']
  email: string
  user_id: string
}

const SATU_EVENT_NAME = 'guru_core_pwa_event_tracker_v1'
const TRACKER_EVENT_NAME = 'GURU_CORE_APP'
const DEFAULT_TIMEOUT = 5000 // 5 seconds

const useSETrackEvent = () => {
  const [, , , getAppDeviceIdValue] = useLocalStorage(
    APP_DEVICE_ID_STORAGE_KEY,
    null
  )
  const { hasSearchQueryFromURL, searchQueryFromURL } = useSearchQuery()
  const contentSearchFilterStore = useContentSearchFilter()
  const contentSearchTrackerStore = useContentSearchTracker()

  const appDeviceID = getAppDeviceIdValue()
  const hasAppDeviceID = Boolean(appDeviceID)
  const androidHandler = AndroidHandler()
  const { session } = useAuth()
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()

  const SETrackEventDefaultAttributes: TSETrackEventDefaultAttributes = {
    is_login: [Boolean(session), 'bool'],
    is_webview: [androidHandler.enabled, 'bool'],
    email: session?.user?.email ?? null,
    user_id: session?.user?.id ?? null,
  }

  const getSource = () => contentSearchTrackerStore.source ?? ''

  const SETrackComponent = (
    activity_type: TSETrackProfileActivities,
    additionalAttributes?: Record<string, any>
  ): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        activity_type,
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAttributes,
          ...additionalAttributes,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  const SETrackSearchPage = (
    activityType,
    additionalAttributes: Record<string, any> = {}
  ): void => {
    const eventTrigger = hasSearchQueryFromURL
      ? 'SEARCH_RESULTS_PAGE'
      : 'SEARCH_PAGE'
    SETrackComponent(activityType, {
      event_trigger: eventTrigger,
      screen_name: eventTrigger,
      source: getSource(),
      ...additionalAttributes,
    })
  }

  const SETrackSearchResultsPage = (
    activityType,
    additionalAttributes: Record<string, any> = {}
  ): void => {
    const selectedFilters = contentSearchFilterStore.getAllSelectedFilters()
    SETrackComponent(activityType, {
      event_trigger: 'SEARCH_RESULTS_PAGE',
      screen_name: 'SEARCH_RESULTS_PAGE',
      source: getSource(),
      text_input: searchQueryFromURL,
      filter: selectedFilters.length > 0 ? selectedFilters.join(' | ') : null,
      ...additionalAttributes,
    })
  }

  return {
    SETrackSearchPage,
    SETrackSearchResultsPage,
  }
}

export default useSETrackEvent
